import React, { useState } from 'react';
import { supabase } from '../services/supabase';
import { Loader2, LogOut, Zap } from 'lucide-react';
import { UserProfile } from '../types';

interface BetaAccessRequestProps {
  user: UserProfile;
  onOrgCreated: (orgId: string) => void;
  onSignOut: () => void;
}

const generateJoinCode = () =>
  Math.random().toString(36).substring(2, 8).toUpperCase();

const BetaAccessRequest: React.FC<BetaAccessRequestProps> = ({ user, onOrgCreated, onSignOut }) => {
  const [name, setName] = useState(user.fullName === 'Coach' ? '' : (user.fullName || ''));
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !orgName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const betaExpiresAt = new Date(now.getFullYear(), now.getMonth() + 7, 0).toISOString();

      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .insert([{
          name: orgName.trim(),
          join_code: generateJoinCode(),
          is_beta_tester: true,
          beta_approval_date: now.toISOString(),
          beta_expires_at: betaExpiresAt,
        }])
        .select()
        .single();
      if (orgError) throw orgError;

      const { error: memberError } = await supabase
        .from('organization_members')
        .insert([{
          organization_id: orgData.id,
          user_id: user.id,
          role: 'head_coach',
          is_admin: true,
          status: 'active',
        }]);
      if (memberError) throw memberError;

      fetch('/api/beta/notify-org-created', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgId: orgData.id,
          orgName: orgName.trim(),
          creatorName: name.trim(),
          creatorEmail: user.email,
        }),
      }).catch(() => {});

      onOrgCreated(orgData.id);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-blue-600 p-8 text-center">
          <h1 className="text-4xl font-bold text-white mb-1">Sideout<span className="text-orange-500">IQ</span></h1>
          <p className="text-sm font-semibold tracking-widest text-white/80 uppercase">Command the Court</p>
        </div>

        <div className="p-8">
          <div className="mb-6 flex items-start gap-3 p-4 bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-100 rounded-xl shadow-sm">
            <Zap className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-700 font-medium leading-relaxed">
              You're signed in with Google — one more step! We're currently in closed beta.
              Request access to secure your spot in the next rollout.
            </p>
          </div>

          <form onSubmit={handleRequest} className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500 uppercase">Your Name</label>
              <input
                type="text"
                required
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Coach Carter"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white text-slate-900 placeholder:text-slate-400"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500 uppercase">Team / Club Name</label>
              <input
                type="text"
                required
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                placeholder="e.g. Coastal Volleyball Club"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white text-slate-900 placeholder:text-slate-400"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500 uppercase">Email</label>
              <p className="px-4 py-2.5 border border-slate-100 rounded-xl bg-slate-50 text-slate-500 text-sm">{user.email}</p>
            </div>

            {error && (
              <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading || !name.trim() || !orgName.trim()}
              className="w-full bg-blue-600 text-white font-bold py-3.5 rounded-xl mt-2 hover:bg-blue-700 active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 disabled:opacity-50"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Request Beta Access
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={onSignOut}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors flex items-center gap-1 mx-auto"
            >
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetaAccessRequest;
